import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTonWallet, useTonConnectUI } from "@tonconnect/ui-react";
import { useAppData } from "../context/AppDataContext";
import { api, type Challenge, type ChallengeMode } from "../lib/api";
import { shareChallenge } from "../lib/share";
import PullToRefresh from "../components/PullToRefresh";

type Duration = "10m" | "1h" | "12h" | "5m" | "30m" | "2h" | "4h" | "24h" | "custom";

const DURATION_MINUTES: Record<Duration, number> = {
  "5m": 5,
  "10m": 10,
  "30m": 30,
  "1h": 60,
  "2h": 120,
  "4h": 240,
  "12h": 720,
  "24h": 1440,
  "custom": 0,
};

const QUICK_DURATIONS: Duration[] = ["10m", "1h", "12h"];
const MORE_DURATIONS: Duration[] = ["5m", "30m", "2h", "4h", "24h", "custom"];

const MAX_CUSTOM_DAYS = 7;

function CreateTab() {
  const navigate = useNavigate();
  const wallet = useTonWallet();
  const [tonConnectUI] = useTonConnectUI();
  const { user, demoMode, refresh, updateBalance, setUser } = useAppData();

  const [description, setDescription] = useState("");
  const [mode, setMode] = useState<ChallengeMode>("BINARY");
  const [side, setSide] = useState<"YES" | "NO">("YES");
  const [customLabelA, setCustomLabelA] = useState("");
  const [customLabelB, setCustomLabelB] = useState("");
  const [stake, setStake] = useState("");
  const [duration, setDuration] = useState<Duration>("1h");
  const [customHours, setCustomHours] = useState("");
  const [showMoreDurations, setShowMoreDurations] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Challenge Duration options
  const [showDurationOptions, setShowDurationOptions] = useState(false);
  const [durationType, setDurationType] = useState<"none" | "timer" | "specific">("none");
  // Timer duration (Option 1)
  const [timerDuration, setTimerDuration] = useState<Duration>("1h");
  const [timerCustomHours, setTimerCustomHours] = useState("");
  const [showMoreTimerDurations, setShowMoreTimerDurations] = useState(false);
  // Specific time (Option 2)
  const [resolutionDate, setResolutionDate] = useState("");
  const [resolutionTime, setResolutionTime] = useState("");

  // Success state
  const [createdChallenge, setCreatedChallenge] = useState<Challenge | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const hasSpecificTime = resolutionDate && resolutionTime;

  const getCloseTime = (): string => {
    let minutes: number;

    if (duration === "custom") {
      const hours = parseFloat(customHours) || 1;
      const maxHours = MAX_CUSTOM_DAYS * 24;
      minutes = Math.min(hours, maxHours) * 60;
    } else {
      minutes = DURATION_MINUTES[duration];
    }

    const closeDate = new Date();
    closeDate.setMinutes(closeDate.getMinutes() + minutes);
    return closeDate.toISOString();
  };

  const getResolutionTime = (): string | undefined => {
    if (durationType !== "specific" || !hasSpecificTime) {
      return undefined;
    }

    const dateTime = new Date(`${resolutionDate}T${resolutionTime}`);
    return dateTime.toISOString();
  };

  const getDurationMinutes = (): number | undefined => {
    if (durationType !== "timer") {
      return undefined;
    }

    if (timerDuration === "custom") {
      const hours = parseFloat(timerCustomHours) || 1;
      return Math.round(hours * 60);
    }

    return DURATION_MINUTES[timerDuration];
  };

  const handleTimerDurationSelect = (d: Duration) => {
    setTimerDuration(d);
    if (QUICK_DURATIONS.includes(d)) {
      setShowMoreTimerDurations(false);
    }
  };

  const handleDurationSelect = (d: Duration) => {
    setDuration(d);
    // If selecting from quick options, collapse the more section
    if (QUICK_DURATIONS.includes(d)) {
      setShowMoreDurations(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!demoMode && !wallet) {
      await tonConnectUI.openModal();
      return;
    }

    if (!description || !stake) {
      setError("Please fill in all required fields");
      return;
    }

    // Validate custom labels for CUSTOM mode
    if (mode === "CUSTOM" && (!customLabelA.trim() || !customLabelB.trim())) {
      setError("Please enter labels for both sides");
      return;
    }

    if (duration === "custom" && (!customHours || parseFloat(customHours) <= 0)) {
      setError("Please enter a valid expiration time");
      return;
    }

    // Validate timer duration
    if (durationType === "timer" && timerDuration === "custom") {
      if (!timerCustomHours || parseFloat(timerCustomHours) <= 0) {
        setError("Please enter a valid challenge duration");
        return;
      }
    }

    // Validate resolution time is in the future
    if (durationType === "specific" && hasSpecificTime) {
      const resTime = new Date(`${resolutionDate}T${resolutionTime}`);
      if (resTime <= new Date()) {
        setError("Resolution time must be in the future");
        return;
      }
    }

    const stakeAmount = parseFloat(stake);
    if (user && stakeAmount > user.balance) {
      setError(`Insufficient balance. You have ${user.balance.toFixed(2)} TON`);
      return;
    }

    setIsSubmitting(true);

    try {
      // For 1v1, we use "direct" as chatId - no group needed
      const chatId = window.Telegram?.WebApp?.initDataUnsafe?.chat?.id?.toString() || "direct";

      const result = await api.createChallenge({
        description,
        side: mode === "VERSUS" ? undefined : side, // VERSUS mode doesn't need side (auto YES)
        stake: stakeAmount,
        chatId,
        closeTime: getCloseTime(),
        resolutionTime: getResolutionTime(),
        durationMinutes: getDurationMinutes(),
        mode,
        labelA: mode === "CUSTOM" ? customLabelA.trim() : undefined,
        labelB: mode === "CUSTOM" ? customLabelB.trim() : undefined,
      });

      if (result.userBalance !== null) {
        updateBalance(result.userBalance);
        setUser(user ? { ...user, balance: result.userBalance } : null);
      }

      setCreatedChallenge(result.challenge);

      // Refresh the global data
      await refresh();
    } catch (err) {
      console.error("Failed to create challenge:", err);
      setError(err instanceof Error ? err.message : "Failed to create challenge");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChallengeFriend = () => {
    if (!createdChallenge) return;
    shareChallenge(createdChallenge.id);
  };

  const handleViewChallenge = () => {
    if (createdChallenge) {
      navigate(`/challenge/${createdChallenge.id}`);
    }
  };

  const handleCreateAnother = () => {
    setCreatedChallenge(null);
    setDescription("");
    setStake("");
    setDuration("1h");
    setCustomHours("");
    setMode("BINARY");
    setSide("YES");
    setCustomLabelA("");
    setCustomLabelB("");
    setShowDurationOptions(false);
    setDurationType("none");
    setTimerDuration("1h");
    setTimerCustomHours("");
    setShowMoreTimerDurations(false);
    setResolutionDate("");
    setResolutionTime("");
  };

  const handleCancel = () => {
    if (!createdChallenge) return;

    const doCancel = async () => {
      setIsCancelling(true);
      try {
        const result = await api.cancelChallenge(createdChallenge.id);
        if (result.userBalance !== null) {
          updateBalance(result.userBalance);
          setUser(user ? { ...user, balance: result.userBalance } : null);
        }
        await refresh();
        // Reset to create form
        handleCreateAnother();
      } catch (err) {
        console.error("Failed to cancel:", err);
        setError(err instanceof Error ? err.message : "Failed to cancel");
        setIsCancelling(false);
      }
    };

    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.showConfirm(
        "Cancel this challenge? Your contribution will be refunded.",
        (confirmed) => {
          if (confirmed) doCancel();
        }
      );
    } else if (confirm("Cancel this challenge? Your contribution will be refunded.")) {
      doCancel();
    }
  };

  // Get the label for the user's side
  const getUserSideLabel = () => {
    if (!createdChallenge) return side;
    // For VERSUS mode, the creator is always on YES side (labelA)
    if (mode === "VERSUS") return createdChallenge.labelA;
    // For CUSTOM mode, use the custom labels
    if (mode === "CUSTOM") return side === "YES" ? customLabelA : customLabelB;
    // For BINARY mode, use Yes/No
    return side === "YES" ? "Yes" : "No";
  };

  // Get timer info text for success screen
  const getTimerInfoText = () => {
    if (durationType === "timer") {
      const mins = getDurationMinutes();
      if (!mins) return null;
      if (mins >= 60) {
        const hours = mins / 60;
        return `${hours}h timer starts when opponent joins`;
      }
      return `${mins}m timer starts when opponent joins`;
    }
    if (durationType === "specific" && hasSpecificTime) {
      const date = new Date(`${resolutionDate}T${resolutionTime}`);
      return `Resolution at ${date.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`;
    }
    return null;
  };

  // Success screen
  if (createdChallenge) {
    return (
      <PullToRefresh onRefresh={refresh}>
        <div className="tab-content create success-screen">
          <div className="success-icon">&#x2705;</div>
          <h1>Challenge Created!</h1>

          <div className="challenge-preview-minimal">
            <p className="preview-description">"{createdChallenge.description}"</p>
            <div className="preview-amount">
              <span className="amount-value">{stake}</span>
              <span className="amount-currency">TON</span>
            </div>
            <p className="preview-side">You're backing <strong>{getUserSideLabel()}</strong></p>
            {getTimerInfoText() && (
              <div className="timer-info-badge">
                <span className="timer-icon">⏱️</span>
                <span>{getTimerInfoText()}</span>
              </div>
            )}
          </div>

          <p className="share-prompt">Challenge a friend to take the other side!</p>

          <div className="success-actions">
            <button className="btn primary full-width" onClick={handleChallengeFriend}>
              Challenge a Friend
            </button>
            <button className="btn secondary full-width" onClick={handleViewChallenge}>
              View Challenge
            </button>
            <button className="btn text full-width" onClick={handleCreateAnother}>
              Create Another
            </button>
            <button
              className="btn text full-width cancel-link"
              onClick={handleCancel}
              disabled={isCancelling}
            >
              {isCancelling ? "Cancelling..." : "Cancel & Refund"}
            </button>
          </div>
        </div>
      </PullToRefresh>
    );
  }

  // Get today's date for min attribute
  const today = new Date().toISOString().split("T")[0];

  // Check if current duration is in "more" section
  const isMoreDuration = MORE_DURATIONS.includes(duration);

  return (
    <PullToRefresh onRefresh={refresh}>
      <div className="tab-content create create-minimal">
        <h1>Create Challenge</h1>

        {error && <div className="error-banner">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Description */}
          <div className="form-group compact">
            <textarea
              placeholder='What&apos;s the challenge? e.g., "Max arrives by 8pm"'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={200}
              rows={2}
              required
            />
          </div>

          {/* Mode picker - compact pills */}
          <div className="form-group compact">
            <div className="mode-pills">
              <button
                type="button"
                className={`mode-pill ${mode === "BINARY" ? "active" : ""}`}
                onClick={() => setMode("BINARY")}
              >
                Yes/No
              </button>
              <button
                type="button"
                className={`mode-pill ${mode === "VERSUS" ? "active" : ""}`}
                onClick={() => setMode("VERSUS")}
              >
                1v1
              </button>
              <button
                type="button"
                className={`mode-pill ${mode === "CUSTOM" ? "active" : ""}`}
                onClick={() => setMode("CUSTOM")}
              >
                Custom
              </button>
            </div>
          </div>

          {/* Side picker - only for BINARY mode, compact */}
          {mode === "BINARY" && (
            <div className="form-group compact">
              <div className="side-pills">
                <button
                  type="button"
                  className={`side-pill ${side === "YES" ? "active" : ""}`}
                  onClick={() => setSide("YES")}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`side-pill ${side === "NO" ? "active" : ""}`}
                  onClick={() => setSide("NO")}
                >
                  No
                </button>
              </div>
            </div>
          )}

          {/* Custom labels - compact inline */}
          {mode === "CUSTOM" && (
            <div className="form-group compact">
              <div className="custom-labels-inline">
                <input
                  type="text"
                  placeholder="Side A"
                  value={customLabelA}
                  onChange={(e) => setCustomLabelA(e.target.value)}
                  maxLength={20}
                />
                <span className="vs">vs</span>
                <input
                  type="text"
                  placeholder="Side B"
                  value={customLabelB}
                  onChange={(e) => setCustomLabelB(e.target.value)}
                  maxLength={20}
                />
              </div>
              {/* Side selection for custom */}
              <div className="side-pills" style={{ marginTop: "8px" }}>
                <button
                  type="button"
                  className={`side-pill ${side === "YES" ? "active" : ""}`}
                  onClick={() => setSide("YES")}
                >
                  {customLabelA || "Side A"}
                </button>
                <button
                  type="button"
                  className={`side-pill ${side === "NO" ? "active" : ""}`}
                  onClick={() => setSide("NO")}
                >
                  {customLabelB || "Side B"}
                </button>
              </div>
            </div>
          )}

          {/* Amount input - compact */}
          <div className="form-group compact">
            <div className="amount-row">
              <input
                type="number"
                placeholder="Amount"
                min="0.1"
                step="0.1"
                value={stake}
                onChange={(e) => setStake(e.target.value)}
                required
              />
              <span className="currency">TON</span>
            </div>
            {user && (
              <span className="balance-hint">Balance: {user.balance.toFixed(0)} TON</span>
            )}
          </div>

          {/* Duration picker - quick options + expandable */}
          <div className="form-group compact">
            <label className="small-label">Expires in</label>
            <div className="duration-row">
              {QUICK_DURATIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`dur-pill ${duration === d ? "active" : ""}`}
                  onClick={() => handleDurationSelect(d)}
                >
                  {d}
                </button>
              ))}
              <button
                type="button"
                className={`dur-pill more ${showMoreDurations || isMoreDuration ? "active" : ""}`}
                onClick={() => setShowMoreDurations(!showMoreDurations)}
              >
                {isMoreDuration && !showMoreDurations ? duration : "More"}
              </button>
            </div>

            {/* Expanded duration options */}
            {showMoreDurations && (
              <div className="duration-expanded">
                {MORE_DURATIONS.filter(d => d !== "custom").map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`dur-pill ${duration === d ? "active" : ""}`}
                    onClick={() => handleDurationSelect(d)}
                  >
                    {d}
                  </button>
                ))}
                <button
                  type="button"
                  className={`dur-pill ${duration === "custom" ? "active" : ""}`}
                  onClick={() => handleDurationSelect("custom")}
                >
                  Custom
                </button>
              </div>
            )}

            {/* Custom hours input */}
            {duration === "custom" && (
              <div className="custom-hours">
                <input
                  type="number"
                  placeholder="Hours"
                  min="0.5"
                  max={MAX_CUSTOM_DAYS * 24}
                  step="0.5"
                  value={customHours}
                  onChange={(e) => setCustomHours(e.target.value)}
                />
                <span>hours</span>
              </div>
            )}
          </div>

          {/* Challenge Duration toggle */}
          <button
            type="button"
            className="advanced-toggle"
            onClick={() => setShowDurationOptions(!showDurationOptions)}
          >
            {showDurationOptions ? "Hide duration" : "Challenge Duration"}
            <span className={`chevron ${showDurationOptions ? "up" : ""}`}>&#x25BC;</span>
          </button>

          {/* Challenge Duration options */}
          {showDurationOptions && (
            <div className="advanced-section">
              <span className="hint-inline">Set a timer for when voting should start</span>

              {/* Duration type selector */}
              <div className="duration-type-pills">
                <button
                  type="button"
                  className={`dur-type-pill ${durationType === "none" ? "active" : ""}`}
                  onClick={() => setDurationType("none")}
                >
                  No timer
                </button>
                <button
                  type="button"
                  className={`dur-type-pill ${durationType === "timer" ? "active" : ""}`}
                  onClick={() => setDurationType("timer")}
                >
                  Timer
                </button>
                <button
                  type="button"
                  className={`dur-type-pill ${durationType === "specific" ? "active" : ""}`}
                  onClick={() => setDurationType("specific")}
                >
                  Specific time
                </button>
              </div>

              {/* Option 1: Timer Duration */}
              {durationType === "timer" && (
                <div className="form-group compact">
                  <label className="small-label">Challenge lasts for</label>
                  <div className="duration-row">
                    {QUICK_DURATIONS.map((d) => (
                      <button
                        key={d}
                        type="button"
                        className={`dur-pill ${timerDuration === d ? "active" : ""}`}
                        onClick={() => handleTimerDurationSelect(d)}
                      >
                        {d}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`dur-pill more ${showMoreTimerDurations || MORE_DURATIONS.includes(timerDuration) ? "active" : ""}`}
                      onClick={() => setShowMoreTimerDurations(!showMoreTimerDurations)}
                    >
                      {MORE_DURATIONS.includes(timerDuration) && !showMoreTimerDurations ? timerDuration : "More"}
                    </button>
                  </div>

                  {showMoreTimerDurations && (
                    <div className="duration-expanded">
                      {MORE_DURATIONS.filter(d => d !== "custom").map((d) => (
                        <button
                          key={d}
                          type="button"
                          className={`dur-pill ${timerDuration === d ? "active" : ""}`}
                          onClick={() => handleTimerDurationSelect(d)}
                        >
                          {d}
                        </button>
                      ))}
                      <button
                        type="button"
                        className={`dur-pill ${timerDuration === "custom" ? "active" : ""}`}
                        onClick={() => handleTimerDurationSelect("custom")}
                      >
                        Custom
                      </button>
                    </div>
                  )}

                  {timerDuration === "custom" && (
                    <div className="custom-hours">
                      <input
                        type="number"
                        placeholder="Hours"
                        min="0.1"
                        max={MAX_CUSTOM_DAYS * 24}
                        step="0.5"
                        value={timerCustomHours}
                        onChange={(e) => setTimerCustomHours(e.target.value)}
                      />
                      <span>hours</span>
                    </div>
                  )}

                  <span className="hint-inline timer-hint">Timer starts when opponent joins</span>
                </div>
              )}

              {/* Option 2: Specific Time */}
              {durationType === "specific" && (
                <div className="form-group compact">
                  <label className="small-label">Voting starts at</label>
                  <div className="datetime-row">
                    <input
                      type="date"
                      value={resolutionDate}
                      onChange={(e) => setResolutionDate(e.target.value)}
                      min={today}
                    />
                    <input
                      type="time"
                      value={resolutionTime}
                      onChange={(e) => setResolutionTime(e.target.value)}
                    />
                  </div>
                  <span className="hint-inline timer-hint">Countdown shows after opponent joins</span>
                </div>
              )}
            </div>
          )}

          <button type="submit" className="btn primary full-width create-btn" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Challenge"}
          </button>
        </form>
      </div>
    </PullToRefresh>
  );
}

export default CreateTab;
